import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableHeadComponent } from "../../common/TableHead";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  Switch,
  TextField,
  Autocomplete,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Input,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { createNewPatchStreamsImmix } from "./EditCameraUtils/createNewPatchStreamsImmix";
import { createNewPatchStreamsDice } from "./EditCameraUtils/createNewPatchStreamsDice";
import { createNewPatchStreamsSureview } from "./EditCameraUtils/createNewPatchStreamsSureview";
import { createNewPatchStreamsSentinel } from "./EditCameraUtils/createNewPatchStreamsSentinel";
import { createNewPatchStreamsBold } from "./EditCameraUtils/createNewPatchStreamsBold";
import { createNewPatchStreamsPatriot } from "./EditCameraUtils/createNewPatchStreamsPatriot";
import { createNewPatchStreamsSoftguard } from "./EditCameraUtils/createNewPatchStreamsSoftguard";
import { useAppStore } from "../../../appStore";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import SelectedStreamDropDown from "./SelectedStreamDropDown";
import { PatriotDeliverySummary } from "../../common/PatriotDrawIgnoreZonesGate";
import {
  PATRIOT_DELIVERY_LABEL,
  PatriotDeliveryChoice,
  PatriotDeliveryValues,
} from "../../common/PatriotDeliveryChoice";
import {
  UpdatedAlertStreamTypes,
  UpdatedAlertStreams,
} from "./EditCameraUtils/editCameraInterfaces";

export const AlertSettingsTabEditMode = ({
  immixAlerts,
  diceAlerts,
  sureviewAlerts,
  setEditStreamWebhookDialogOpen,
  sentinelAlerts,
  boldAlerts,
  patriotAlerts,
  softguardAlerts,
  immixAlertsOnCamera,
  diceAlertsOnCamera,
  sureViewAlertsOnCamera,
  siteInfo,
  webhooksAlertsOnCamera,
  sentinelAlertsOnCamera,
  boldAlertsOnCamera,
  patriotAlertsOnCamera,
  softguardAlertsOnCamera,
  evalinkAlertsOnCamera,
  cameraGeneralInfo,
  setPatchObject,
  setGeneralInfoChangesMade,
  setAddStreamWebhookDialogOpen,
}: {
  immixAlerts: any;
  diceAlerts: any;
  sureviewAlerts: any;
  setEditStreamWebhookDialogOpen: any;
  sentinelAlerts: any;
  boldAlerts: any;
  patriotAlerts: any;
  softguardAlerts: any;
  immixAlertsOnCamera: boolean;
  diceAlertsOnCamera: boolean;
  sureViewAlertsOnCamera: boolean;
  siteInfo: any;
  webhooksAlertsOnCamera: boolean;
  sentinelAlertsOnCamera: boolean;
  boldAlertsOnCamera: boolean;
  patriotAlertsOnCamera: boolean;
  softguardAlertsOnCamera: boolean;
  evalinkAlertsOnCamera: boolean;
  cameraGeneralInfo: any;
  setPatchObject: any;
  setGeneralInfoChangesMade: any;
  setAddStreamWebhookDialogOpen: any;
}) => {
  const { userPermissions } = useAppStore();

  const {
    setDeleteWebhookDialogOpen,
    setWebhookIdToDelete,
    webhooksData,
    cameraStreams,
    cameraStreamsSelectedIndex,
    cameraStreamsObject,
    cameraStreamsPatchObject,
    setCameraStreamsPatchObject,
    setUpdatedAlertStreams,
    setUpdatedAlertStreamTypes,
    sysaidAlertsOnCamera,
  } = useEditCameraStore();

  const sureviewServerOptions = [
    ["US SMTP Server", "us-smtp.sureviewops.com"],
    ["EU SMTP Server", "eu-smtp.sureviewops.com"],
  ];
  const sureViewOptionNames = new Map();
  sureviewServerOptions.forEach((option: any) => {
    sureViewOptionNames.set(option[1], option[0]);
  });
  const sureViewOptionIds = new Map();
  sureviewServerOptions.forEach((option: any) => {
    sureViewOptionIds.set(option[0], option[1]);
  });

  const webhooksTitlesArray = ["Webhook", "Custom Fields", "Event Type"];

  const handleConvertTypeToString = (string: any) => {
    if (string === "no_motion") {
      return "No Motion";
    } else if (string === "unable_to_connect") {
      return "Unable to Connect";
    } else if (string === "detection") {
      return "Detection";
    } else return "";
  };

  // State for password visibility
  const [showPasswords, setShowPasswords] = React.useState<{
    [key: string]: boolean;
  }>({});

  // Local state for Immix alerts to prevent re-renders
  const [localImmixAlerts, setLocalImmixAlerts] = useState<any>({});

  // Local state for Dice alerts to prevent re-renders
  const [localDiceAlerts, setLocalDiceAlerts] = useState<any>({});

  const [localSureviewAlerts, setLocalSureviewAlerts] = useState<any>({});

  const [localSentinelAlerts, setLocalSentinelAlerts] = useState<any>({});

  const [localBoldAlerts, setLocalBoldAlerts] = useState<any>({});

  const [localPatriotAlerts, setLocalPatriotAlerts] = useState<any>({});

  const [localSoftguardAlerts, setLocalSoftguardAlerts] = useState<any>({});

  const [localSysaidAlerts, setLocalSysaidAlerts] = useState<any>({});

  const [localEvalinkAlerts, setLocalEvalinkAlerts] = useState<any>({});

  // Initialize local state when cameraStreamsPatchObject changes
  useEffect(() => {
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.immix_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.immix_alerts;
      if (alerts) {
        const localAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localAlerts[alert.id] = {
              immix_server: alert.immix_server || "",
              immix_port: alert.immix_port || "",
              immix_email: alert.immix_email || "",
              immix_input1: alert.immix_input1 || "",
              draw_ignore_zones: alert.draw_ignore_zones || false,
            };
          }
        });
        setLocalImmixAlerts(localAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.dice_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.dice_alerts;
      if (alerts) {
        const localAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localAlerts[alert.id] = {
              dice_server: alert.dice_server || "",
              dice_port: alert.dice_port || "",
              dice_email: alert.dice_email || "",
              dice_input1: alert.dice_input1 || "",
              draw_ignore_zones: alert.draw_ignore_zones || false,
            };
          }
        });
        setLocalDiceAlerts(localAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.sureview_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.sureview_alerts;
      if (alerts) {
        const localSureviewAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localSureviewAlerts[alert.id] = {
              sureview_server: alert.sureview_server || "",
              sureview_subject: alert.sureview_subject || "",
              sureview_cc: alert.sureview_cc || "",
              sureview_email: alert.sureview_email || "",
            };
          }
        });
        setLocalSureviewAlerts(localSureviewAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.sentinel_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.sentinel_alerts;
      if (alerts) {
        const localSentinelAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localSentinelAlerts[alert.id] = {
              sentinel_server: alert.sentinel_server || "",
              sentinel_site_id: alert.sentinel_site_id || "",
              sentinel_device_address: alert.sentinel_device_address || "",
              draw_ignore_zones: alert.draw_ignore_zones || false,
            };
          }
        });
        setLocalSentinelAlerts(localSentinelAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.bold_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.bold_alerts;
      if (alerts) {
        const localBoldAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localBoldAlerts[alert.id] = {
              bold_server: alert.bold_server || "",
              bold_port: alert.bold_port || "",
              bold_id: alert.bold_id || "",
              bold_sdnis: alert.bold_sdnis || "",
              bold_zone: alert.bold_zone || "",
              bold_alarmtype: alert.bold_alarmtype || "",
            };
          }
        });
        setLocalBoldAlerts(localBoldAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.patriot_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.patriot_alerts;
      if (alerts) {
        const localPatriotAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localPatriotAlerts[alert.id] = {
              patriot_server: alert.patriot_server || "",
              patriot_client_no: alert.patriot_client_no || "",
              patriot_username: alert.patriot_username || "",
              patriot_password: alert.patriot_password || "",
              patriot_zone_number: alert.patriot_zone_number || "",
              patriot_send_video: alert.patriot_send_video || false,
              patriot_type_number: alert.patriot_type_number || "",
              draw_ignore_zones: alert.draw_ignore_zones || false,
            };
          }
        });
        setLocalPatriotAlerts(localPatriotAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.softguard_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.softguard_alerts;
      if (alerts) {
        const localSoftguardAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localSoftguardAlerts[alert.id] = {
              server: alert.server || "",
              port: alert.port || "",
              zone: alert.zone || "",
              account: alert.account || "",
              user: alert.user || "",
              user_ext: alert.user_ext || "",
            };
          }
        });
        setLocalSoftguardAlerts(localSoftguardAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.sysaid_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.sysaid_alerts;
      if (alerts) {
        const localSysaidAlerts: any = {};
        alerts.forEach((alert: any, index: number) => {
          if (alert?.id) {
            localSysaidAlerts[alert.id] = {
              sysaid_server: alert.sysaid_server || "",
              sysaid_client_local: alert.sysaid_client_local || "",
              sysaid_username: alert.sysaid_username || "",
              sysaid_password: alert.sysaid_password || "",
            };
          }
        });
        setLocalSysaidAlerts(localSysaidAlerts);
      }
    }
    if (
      cameraStreamsPatchObject &&
      cameraStreamsObject[cameraStreamsSelectedIndex]?.evalink_alerts
    ) {
      const alerts =
        cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.evalink_alerts;
      if (alerts) {
        const localEvalinkAlertsData: any = {};
        alerts.forEach((alert: any) => {
          if (alert?.id) {
            localEvalinkAlertsData[alert.id] = {
              evalink_token: alert.evalink_token || "",
              evalink_device_id: alert.evalink_device_id || "",
              evalink_company_id: alert.evalink_company_id || "",
              partition_id: alert.partition_id || "",
              alarm_zone_id: alert.alarm_zone_id || "",
              active: alert.active ?? true,
            };
          }
        });
        setLocalEvalinkAlerts(localEvalinkAlertsData);
      }
    }
  }, [
    cameraStreamsPatchObject,
    cameraStreamsSelectedIndex,
    cameraStreamsObject,
    cameraStreams,
  ]);

  const handleClickShowPassword = (passwordId: string) => {
    setShowPasswords((prev) => ({
      ...prev,
      [passwordId]: !prev[passwordId],
    }));
  };

  React.useEffect(() => {
    setPatchObject((previousState: any) => {
      return { ...previousState, streams: cameraGeneralInfo.streams };
    });
  }, []);

  return (
    <div className="relative w-full min-w-[1150px]">
      {cameraStreams && cameraStreams.length > 1 && (
        <div className="bg-white">
          <SelectedStreamDropDown />
        </div>
      )}
      {immixAlertsOnCamera && (
        <div key="immixAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Immix Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.immix_alerts
              ?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.immix_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Immix Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                        aria-label="simple table"
                      >
                        <TableHeadComponent
                          titlesArray={[
                            "Immix Server",
                            "Immix Port",
                            "Immix Email",
                            "Immix input 1",
                            "Draw Ignore Zones",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.immix_server}>
                                <TextField
                                  id={object.immix_server}
                                  value={
                                    localImmixAlerts[object.id]?.immix_server ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalImmixAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        immix_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        immix: {
                                          ...prev.immix,
                                          [object.id]: {
                                            ...localImmixAlerts?.[object.id],
                                            immix_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        immix: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.immix_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.immix_server =
                                          localImmixAlerts[object.id]
                                            ?.immix_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.immix_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.immix_port}>
                                <TextField
                                  id={object.immix_port}
                                  value={
                                    localImmixAlerts[object.id]?.immix_port ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalImmixAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        immix_port: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        immix: {
                                          ...prev.immix,
                                          [object.id]: {
                                            ...localImmixAlerts?.[object.id],
                                            immix_port: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        immix: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.immix_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.immix_port =
                                          localImmixAlerts[object.id]
                                            ?.immix_port || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.immix_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.immix_email}>
                                <TextField
                                  id={object.immix_email}
                                  value={
                                    localImmixAlerts[object.id]?.immix_email ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalImmixAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        immix_email: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        immix: {
                                          ...prev.immix,
                                          [object.id]: {
                                            ...localImmixAlerts?.[object.id],
                                            immix_email: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        immix: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.immix_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.immix_email =
                                          localImmixAlerts[object.id]
                                            ?.immix_email || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.immix_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.immix_input1}>
                                <TextField
                                  id={object.immix_input1}
                                  value={
                                    localImmixAlerts[object.id]?.immix_input1 ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalImmixAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        immix_input1: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        immix: {
                                          ...prev.immix,
                                          [object.id]: {
                                            ...localImmixAlerts?.[object.id],
                                            immix_input1: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        immix: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.immix_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.immix_input1 =
                                          localImmixAlerts[object.id]
                                            ?.immix_input1 || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.immix_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.draw_ignore_zones}>
                                <Switch
                                  id={object.draw_ignore_zones}
                                  checked={
                                    localImmixAlerts[object.id]
                                      ?.draw_ignore_zones || false
                                  }
                                  color="warning"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    checked: boolean
                                  ) => {
                                    setLocalImmixAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        draw_ignore_zones: checked,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        immix: {
                                          ...prev.immix,
                                          [object.id]: {
                                            ...localImmixAlerts?.[object.id],
                                            draw_ignore_zones: checked,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        immix: true,
                                      })
                                    );
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.immix_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.draw_ignore_zones = checked;
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.immix_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {diceAlertsOnCamera && (
        <div key="diceAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Dice Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.dice_alerts
              ?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.dice_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Dice Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                        aria-label="simple table"
                      >
                        <TableHeadComponent
                          titlesArray={[
                            "Dice Server",
                            "Dice Port",
                            "Dice Email",
                            "Dice input 1",
                            "Draw Ignore Zones",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.dice_server}>
                                <TextField
                                  id={object.dice_server}
                                  value={
                                    localDiceAlerts[object.id]?.dice_server ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalDiceAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        dice_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        dice: {
                                          ...prev.dice,
                                          [object.id]: {
                                            ...localDiceAlerts?.[object.id],
                                            dice_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        dice: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.dice_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.dice_server =
                                          localDiceAlerts[object.id]
                                            ?.dice_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.dice_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.dice_port}>
                                <TextField
                                  id={object.dice_port}
                                  value={
                                    localDiceAlerts[object.id]?.dice_port || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalDiceAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        dice_port: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        dice: {
                                          ...prev.dice,
                                          [object.id]: {
                                            ...localDiceAlerts?.[object.id],
                                            dice_port: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        dice: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.dice_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.dice_port =
                                          localDiceAlerts[object.id]
                                            ?.dice_port || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.dice_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.dice_email}>
                                <TextField
                                  id={object.dice_email}
                                  value={
                                    localDiceAlerts[object.id]?.dice_email || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalDiceAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        dice_email: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        dice: {
                                          ...prev.dice,
                                          [object.id]: {
                                            ...localDiceAlerts?.[object.id],
                                            dice_email: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        dice: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.dice_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.dice_email =
                                          localDiceAlerts[object.id]
                                            ?.dice_email || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.dice_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.dice_input1}>
                                <TextField
                                  id={object.dice_input1}
                                  value={
                                    localDiceAlerts[object.id]?.dice_input1 ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalDiceAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        dice_input1: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        dice: {
                                          ...prev.dice,
                                          [object.id]: {
                                            ...localDiceAlerts?.[object.id],
                                            dice_input1: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        dice: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.dice_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.dice_input1 =
                                          localDiceAlerts[object.id]
                                            ?.dice_input1 || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.dice_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.draw_ignore_zones}>
                                <Switch
                                  id={object.draw_ignore_zones}
                                  checked={
                                    localDiceAlerts[object.id]
                                      ?.draw_ignore_zones || false
                                  }
                                  color="warning"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    checked: boolean
                                  ) => {
                                    setLocalDiceAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        draw_ignore_zones: checked,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        dice: {
                                          ...prev.dice,
                                          [object.id]: {
                                            ...localDiceAlerts?.[object.id],
                                            draw_ignore_zones: checked,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        dice: true,
                                      })
                                    );
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.dice_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.draw_ignore_zones = checked;
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.dice_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {sureViewAlertsOnCamera && (
        <div key="sureviewAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Sureview Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]
              ?.sureview_alerts?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.sureview_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Sureview Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                        aria-label="simple table"
                      >
                        <TableHeadComponent
                          titlesArray={[
                            "Sureview Server",
                            "Sureview Subject",
                            "Sureview cc",
                            "Sureview Email",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sureview_server}>
                                <Autocomplete
                                  disablePortal
                                  id={object.sureview_server}
                                  options={sureviewServerOptions.map(
                                    (option: string[]) => {
                                      return option[0];
                                    }
                                  )}
                                  value={
                                    sureViewOptionNames.get(
                                      localSureviewAlerts[object.id]
                                        ?.sureview_server || ""
                                    ) || ""
                                  }
                                  onChange={(
                                    event: any,
                                    newInputValue: any
                                  ) => {
                                    const convertedInputValue =
                                      sureViewOptionIds.get(newInputValue);
                                    setLocalSureviewAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sureview_server:
                                          convertedInputValue || "",
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sureview: {
                                          ...prev.sureview,
                                          [object.id]: {
                                            ...localSureviewAlerts?.[object.id],
                                            sureview_server:
                                              convertedInputValue,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sureview: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sureview_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sureview_server =
                                          localSureviewAlerts[object.id]
                                            ?.sureview_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sureview_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                  sx={{ width: 350 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="standard"
                                      label="Sureview Server"
                                      autoComplete="off"
                                      id={object.sureview_server}
                                      name={object.sureview_server}
                                    />
                                  )}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sureview_subject}>
                                <TextField
                                  id={object.sureview_subject}
                                  value={
                                    localSureviewAlerts[object.id]
                                      ?.sureview_subject || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSureviewAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sureview_subject: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sureview: {
                                          ...prev.sureview,
                                          [object.id]: {
                                            ...localSureviewAlerts?.[object.id],
                                            sureview_subject:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sureview: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sureview_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sureview_subject =
                                          localSureviewAlerts[object.id]
                                            ?.sureview_subject || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sureview_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sureview_cc}>
                                <TextField
                                  id={object.sureview_cc}
                                  value={
                                    localSureviewAlerts[object.id]
                                      ?.sureview_cc || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSureviewAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sureview_cc: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sureview: {
                                          ...prev.sureview,
                                          [object.id]: {
                                            ...localSureviewAlerts?.[object.id],
                                            sureview_cc: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sureview: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sureview_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sureview_cc =
                                          localSureviewAlerts[object.id]
                                            ?.sureview_cc || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sureview_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sureview_email}>
                                <TextField
                                  id={object.sureview_email}
                                  value={
                                    localSureviewAlerts[object.id]
                                      ?.sureview_email || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSureviewAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sureview_email: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sureview: {
                                          ...prev.sureview,
                                          [object.id]: {
                                            ...localSureviewAlerts?.[object.id],
                                            sureview_email: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sureview: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sureview_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sureview_email =
                                          localSureviewAlerts[object.id]
                                            ?.sureview_email || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sureview_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {(webhooksData || siteInfo.webhooks || webhooksAlertsOnCamera) && (
        <div key="webhooks" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white flex relative">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Webhooks
            </Typography>
            <div className="absolute right-[70px] pt-[10px]">
              {userPermissions.edit_cameras && (
                <OrangeTextButton
                  onClick={() => {
                    setAddStreamWebhookDialogOpen(true);
                  }}
                  text="+ Add Stream Webhook"
                  disabled={false}
                />
              )}
            </div>
          </div>
          {webhooksData &&
            webhooksData[0] &&
            webhooksData
              .filter(
                (object: any) =>
                  cameraStreams &&
                  cameraStreams[cameraStreamsSelectedIndex] &&
                  object.stream.id ===
                    cameraStreams[cameraStreamsSelectedIndex].id
              )
              .map((object: any, index: any) => {
                return (
                  <div key={object.id} className="h-[168px]">
                    <div className="min-w-[1150px] h-[60px] bg-white">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Stream Webhook {index + 1}
                        <OrangeTextButton
                          onClick={() => {
                            localStorage.setItem(
                              "selectedWebhook",
                              JSON.stringify(object)
                            );
                            setEditStreamWebhookDialogOpen(true);
                          }}
                          text={
                            <>
                              <EditIcon fontSize="small" />
                            </>
                          }
                          disabled={false}
                        />
                        <OrangeTextButton
                          onClick={() => {
                            setDeleteWebhookDialogOpen(true);
                            setWebhookIdToDelete(object.id);
                          }}
                          text={
                            <>
                              <DeleteIcon fontSize="small" />
                            </>
                          }
                          disabled={false}
                        />
                      </Typography>
                    </div>
                    <div>
                      <TableContainer component={Paper} sx={{ width: "100%" }}>
                        <Table
                          sx={{
                            width: "100%",
                            fontFamily: "Mulish",
                            tableLayout: "fixed",
                          }}
                        >
                          <TableHeadComponent
                            titlesArray={webhooksTitlesArray}
                          />
                          <TableBody>
                            <TableRow>
                              <TableCell
                                align="left"
                                sx={{
                                  fontFamily: "Mulish",
                                  width: "33.33%",
                                  wordWrap: "break-word",
                                }}
                              >
                                {object.webhook.display_name
                                  ? object.webhook.display_name
                                  : ""}
                              </TableCell>
                              <TableCell
                                align="left"
                                sx={{
                                  fontFamily: "Mulish",
                                  width: "33.33%",
                                  wordWrap: "break-word",
                                }}
                              >
                                {object.custom_fields
                                  ? `${JSON.stringify(object.custom_fields)}`
                                  : ""}
                              </TableCell>
                              <TableCell
                                align="left"
                                sx={{
                                  fontFamily: "Mulish",
                                  width: "33.33%",
                                  wordWrap: "break-word",
                                }}
                              >
                                {object.event_types
                                  ? object.event_types[0]
                                    ? //map object.event_types and return each one using handleConvertTypeToString separated by a comma
                                      object.event_types.map(
                                        (
                                          eventType: any,
                                          eventIndex: number
                                        ) => {
                                          const isLast =
                                            eventIndex ===
                                            object.event_types.length - 1;
                                          return `${handleConvertTypeToString(
                                            eventType
                                          )}${isLast ? "" : ", "}`;
                                        }
                                      )
                                    : ""
                                  : ""}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </div>
                );
              })}
        </div>
      )}
      {sentinelAlertsOnCamera && (
        <div key="sentinelAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Sentinel Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]
              ?.sentinel_alerts?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.sentinel_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Sentinel Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                        aria-label="simple table"
                      >
                        <TableHeadComponent
                          titlesArray={[
                            "Sentinel Server",
                            "Sentinel Site Id",
                            "Sentinel Device Address",
                            "Draw Ignore Zones",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sentinel_server}>
                                <TextField
                                  id={object.sentinel_server}
                                  value={
                                    localSentinelAlerts[object.id]
                                      ?.sentinel_server || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSentinelAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sentinel_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sentinel: {
                                          ...prev.sentinel,
                                          [object.id]: {
                                            ...localSentinelAlerts?.[object.id],
                                            sentinel_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sentinel: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sentinel_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sentinel_server =
                                          localSentinelAlerts[object.id]
                                            ?.sentinel_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sentinel_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sentinel_site_id}>
                                <TextField
                                  id={object.sentinel_site_id}
                                  value={
                                    localSentinelAlerts[object.id]
                                      ?.sentinel_site_id || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSentinelAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sentinel_site_id: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sentinel: {
                                          ...prev.sentinel,
                                          [object.id]: {
                                            ...localSentinelAlerts?.[object.id],
                                            sentinel_site_id:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sentinel: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sentinel_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sentinel_site_id =
                                          localSentinelAlerts[object.id]
                                            ?.sentinel_site_id || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sentinel_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sentinel_device_address}>
                                <TextField
                                  id={object.sentinel_device_address}
                                  value={
                                    localSentinelAlerts[object.id]
                                      ?.sentinel_device_address || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSentinelAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sentinel_device_address:
                                          event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sentinel: {
                                          ...prev.sentinel,
                                          [object.id]: {
                                            ...localSentinelAlerts?.[object.id],
                                            sentinel_device_address:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sentinel_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sentinel_device_address =
                                          localSentinelAlerts[object.id]
                                            ?.sentinel_device_address || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sentinel_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.draw_ignore_zones}>
                                <Switch
                                  id={object.draw_ignore_zones}
                                  checked={
                                    localSentinelAlerts[object.id]
                                      ?.draw_ignore_zones || false
                                  }
                                  color="warning"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                    checked: boolean
                                  ) => {
                                    setLocalSentinelAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        draw_ignore_zones: checked,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sentinel: {
                                          ...prev.sentinel,
                                          [object.id]: {
                                            ...localSentinelAlerts?.[object.id],
                                            draw_ignore_zones: checked,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sentinel: true,
                                      })
                                    );
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sentinel_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.draw_ignore_zones = checked;
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sentinel_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {boldAlertsOnCamera && (
        <div key="boldAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Bold Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.bold_alerts
              ?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.bold_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Bold Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 1000, fontFamily: "Mulish" }}>
                        <TableHeadComponent
                          titlesArray={[
                            "Bold Server",
                            "Bold port",
                            "Bold id",
                            "Bold sdnis",
                            "Bold Zone",
                            "Bold Alarm Type",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_server}>
                                <TextField
                                  id={object.bold_server}
                                  value={
                                    localBoldAlerts[object.id]?.bold_server ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_server =
                                          localBoldAlerts[object.id]
                                            ?.bold_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_port}>
                                <TextField
                                  id={object.bold_port}
                                  value={
                                    localBoldAlerts[object.id]?.bold_port || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_port: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_port: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_port =
                                          localBoldAlerts[object.id]
                                            ?.bold_port || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_id}>
                                <TextField
                                  id={object.bold_id}
                                  value={
                                    localBoldAlerts[object.id]?.bold_id || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_id: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_id: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_id =
                                          localBoldAlerts[object.id]?.bold_id ||
                                          "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_sdnis}>
                                <TextField
                                  id={object.bold_sdnis}
                                  value={
                                    localBoldAlerts[object.id]?.bold_sdnis || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_sdnis: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_sdnis: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_sdnis =
                                          localBoldAlerts[object.id]
                                            ?.bold_sdnis || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_zone}>
                                <TextField
                                  id={object.bold_zone}
                                  value={
                                    localBoldAlerts[object.id]?.bold_zone || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_zone: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_zone: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_zone =
                                          localBoldAlerts[object.id]
                                            ?.bold_zone || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.bold_alarmtype}>
                                <TextField
                                  id={object.bold_alarmtype}
                                  value={
                                    localBoldAlerts[object.id]
                                      ?.bold_alarmtype || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalBoldAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        bold_alarmtype: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        bold: {
                                          ...prev.bold,
                                          [object.id]: {
                                            ...localBoldAlerts?.[object.id],
                                            bold_alarmtype: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        bold: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.bold_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.bold_alarmtype =
                                          localBoldAlerts[object.id]
                                            ?.bold_alarmtype || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.bold_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {patriotAlertsOnCamera && (
        <div key="patriotAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Patriot Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]
              ?.patriot_alerts?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.patriot_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="min-h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Patriot Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 1000, fontFamily: "Mulish" }}>
                        <TableHeadComponent
                          titlesArray={[
                            "Patriot Server",
                            "Patriot client no",
                            "Patriot username",
                            "Patriot password",
                            "Patriot zone number",
                            "Patriot type number",
                            PATRIOT_DELIVERY_LABEL,
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_server}>
                                <TextField
                                  id={object.patriot_server}
                                  value={
                                    localPatriotAlerts[object.id]
                                      ?.patriot_server || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalPatriotAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        patriot_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        patriot: {
                                          ...prev.patriot,
                                          [object.id]: {
                                            ...localPatriotAlerts?.[object.id],
                                            patriot_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        patriot: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.patriot_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.patriot_server =
                                          localPatriotAlerts[object.id]
                                            ?.patriot_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.patriot_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_client_no}>
                                <TextField
                                  id={object.patriot_client_no}
                                  value={
                                    localPatriotAlerts[object.id]
                                      ?.patriot_client_no || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalPatriotAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        patriot_client_no: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        patriot: {
                                          ...prev.patriot,
                                          [object.id]: {
                                            ...localPatriotAlerts?.[object.id],
                                            patriot_client_no:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        patriot: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.patriot_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.patriot_client_no =
                                          localPatriotAlerts[object.id]
                                            ?.patriot_client_no || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.patriot_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_username}>
                                <TextField
                                  id={object.patriot_username}
                                  value={
                                    localPatriotAlerts[object.id]
                                      ?.patriot_username || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalPatriotAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        patriot_username: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        patriot: {
                                          ...prev.patriot,
                                          [object.id]: {
                                            ...localPatriotAlerts?.[object.id],
                                            patriot_username:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        patriot: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.patriot_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.patriot_username =
                                          localPatriotAlerts[object.id]
                                            ?.patriot_username || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.patriot_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_password}>
                                <FormControl
                                  variant="standard"
                                  sx={{ m: 1, width: 150 }}
                                >
                                  <InputLabel htmlFor="standard-adornment-password">
                                    Password
                                  </InputLabel>
                                  <Input
                                    id="standard-adornment-password"
                                    type={
                                      showPasswords[object.id]
                                        ? "text"
                                        : "password"
                                    }
                                    value={
                                      localPatriotAlerts[object.id]
                                        ?.patriot_password || ""
                                    }
                                    onChange={(event) => {
                                      setLocalPatriotAlerts((prev: any) => ({
                                        ...prev,
                                        [object.id]: {
                                          ...prev[object.id],
                                          patriot_password: event.target.value,
                                        },
                                      }));
                                      setUpdatedAlertStreams(
                                        (prev: UpdatedAlertStreams) => ({
                                          ...prev,
                                          patriot: {
                                            ...prev.patriot,
                                            [object.id]: {
                                              ...localPatriotAlerts?.[
                                                object.id
                                              ],
                                              patriot_password:
                                                event.target.value,
                                            },
                                          },
                                        })
                                      );
                                      setUpdatedAlertStreamTypes(
                                        (prev: UpdatedAlertStreamTypes) => ({
                                          ...prev,
                                          patriot: true,
                                        })
                                      );
                                    }}
                                    onBlur={() => {
                                      setCameraStreamsPatchObject(
                                        (previousState: any) => {
                                          const newState = [
                                            ...(previousState || []),
                                          ];
                                          const newStream = {
                                            ...newState[
                                              cameraStreamsSelectedIndex
                                            ],
                                          };
                                          const newAlerts = [
                                            ...(newStream.patriot_alerts || []),
                                          ];
                                          const newAlert = {
                                            ...newAlerts[currentIndex],
                                          };
                                          newAlert.patriot_password =
                                            localPatriotAlerts[object.id]
                                              ?.patriot_password || "";
                                          newAlerts[currentIndex] = newAlert;
                                          newStream.patriot_alerts = newAlerts;
                                          newState[cameraStreamsSelectedIndex] =
                                            newStream;
                                          return newState;
                                        }
                                      );
                                      // setStreamChangesMade(true);
                                    }}
                                    endAdornment={
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="toggle password visibility"
                                          onClick={() =>
                                            handleClickShowPassword(object.id)
                                          }
                                          onMouseDown={(event) => {
                                            event.preventDefault();
                                          }}
                                          edge="end"
                                        >
                                          {showPasswords[object.id] ? (
                                            <VisibilityOff />
                                          ) : (
                                            <Visibility />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    }
                                  />
                                </FormControl>
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_zone_number}>
                                <TextField
                                  id={object.patriot_zone_number}
                                  value={
                                    localPatriotAlerts[object.id]
                                      ?.patriot_zone_number || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalPatriotAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        patriot_zone_number: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        patriot: {
                                          ...prev.patriot,
                                          [object.id]: {
                                            ...localPatriotAlerts?.[object.id],
                                            patriot_zone_number:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        patriot: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.patriot_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.patriot_zone_number =
                                          localPatriotAlerts[object.id]
                                            ?.patriot_zone_number || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.patriot_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.patriot_type_number}>
                                <TextField
                                  id={object.patriot_type_number}
                                  value={
                                    localPatriotAlerts[object.id]
                                      ?.patriot_type_number || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalPatriotAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        patriot_type_number: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        patriot: {
                                          ...prev.patriot,
                                          [object.id]: {
                                            ...localPatriotAlerts?.[object.id],
                                            patriot_type_number:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        patriot: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.patriot_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.patriot_type_number =
                                          localPatriotAlerts[object.id]
                                            ?.patriot_type_number || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.patriot_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <PatriotDeliveryChoice
                                name={`patriot-delivery-${object.id}`}
                                showLabel={false}
                                sendVideo={
                                  localPatriotAlerts[object.id]
                                    ?.patriot_send_video
                                }
                                drawIgnoreZones={
                                  localPatriotAlerts[object.id]
                                    ?.draw_ignore_zones
                                }
                                onChange={(values: PatriotDeliveryValues) => {
                                  setLocalPatriotAlerts((prev: any) => ({
                                    ...prev,
                                    [object.id]: {
                                      ...prev[object.id],
                                      ...values,
                                    },
                                  }));
                                  setUpdatedAlertStreams(
                                    (prev: UpdatedAlertStreams) => ({
                                      ...prev,
                                      patriot: {
                                        ...prev.patriot,
                                        [object.id]: {
                                          ...localPatriotAlerts?.[object.id],
                                          ...values,
                                        },
                                      },
                                    })
                                  );
                                  setUpdatedAlertStreamTypes(
                                    (prev: UpdatedAlertStreamTypes) => ({
                                      ...prev,
                                      patriot: true,
                                    })
                                  );
                                  setCameraStreamsPatchObject(
                                    (previousState: any) => {
                                      const newState = [
                                        ...(previousState || []),
                                      ];
                                      const newStream = {
                                        ...newState[cameraStreamsSelectedIndex],
                                      };
                                      const newAlerts = [
                                        ...(newStream.patriot_alerts || []),
                                      ];
                                      newAlerts[currentIndex] = {
                                        ...newAlerts[currentIndex],
                                        ...values,
                                      };
                                      newStream.patriot_alerts = newAlerts;
                                      newState[cameraStreamsSelectedIndex] =
                                        newStream;
                                      return newState;
                                    }
                                  );
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <PatriotDeliverySummary
                      sendVideo={
                        localPatriotAlerts[object.id]?.patriot_send_video
                      }
                      drawIgnoreZones={
                        localPatriotAlerts[object.id]?.draw_ignore_zones
                      }
                    />
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {softguardAlertsOnCamera && (
        <div key="softguardAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Softguard Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]
              ?.softguard_alerts?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.softguard_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Softguard Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 1000, fontFamily: "Mulish" }}>
                        <TableHeadComponent
                          titlesArray={[
                            "Server",
                            "Port",
                            "Zone",
                            "Account",
                            "User",
                            "User Ext",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.server}>
                                <TextField
                                  id={object.server}
                                  value={
                                    localSoftguardAlerts[object.id]?.server ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.server =
                                          localSoftguardAlerts[object.id]
                                            ?.server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.port}>
                                <TextField
                                  id={object.port}
                                  value={
                                    localSoftguardAlerts[object.id]?.port || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        port: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            port: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.port =
                                          localSoftguardAlerts[object.id]
                                            ?.port || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.zone}>
                                <TextField
                                  id={object.zone}
                                  value={
                                    localSoftguardAlerts[object.id]?.zone || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        zone: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            zone: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.zone =
                                          localSoftguardAlerts[object.id]
                                            ?.zone || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.account}>
                                <TextField
                                  id={object.account}
                                  value={
                                    localSoftguardAlerts[object.id]?.account ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        account: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            account: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.account =
                                          localSoftguardAlerts[object.id]
                                            ?.account || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.user}>
                                <TextField
                                  id={object.user}
                                  value={
                                    localSoftguardAlerts[object.id]?.user || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        user: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            user: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.user =
                                          localSoftguardAlerts[object.id]
                                            ?.user || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.user_ext}>
                                <TextField
                                  id={object.user_ext}
                                  value={
                                    localSoftguardAlerts[object.id]?.user_ext ||
                                    ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSoftguardAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        user_ext: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        softguard: {
                                          ...prev.softguard,
                                          [object.id]: {
                                            ...localSoftguardAlerts?.[
                                              object.id
                                            ],
                                            user_ext: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        softguard: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.softguard_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.user_ext =
                                          localSoftguardAlerts[object.id]
                                            ?.user_ext || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.softguard_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {evalinkAlertsOnCamera && (
        <div key="evalinkAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              Evalink Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]
              ?.evalink_alerts?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.evalink_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[148px]">
                  <div className="min-w-[1150px] h-[40px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        Evalink Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                        aria-label="simple table"
                      >
                        <TableHeadComponent
                          titlesArray={[
                            "Evalink Token",
                            "Evalink Device ID",
                            "Evalink Company ID",
                            "Partition ID",
                            "Alarm Zone ID",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              {object.evalink_token || "--"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              {object.evalink_device_id || "--"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              {object.evalink_company_id || "--"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <TextField
                                value={
                                  localEvalinkAlerts[object.id]?.partition_id ||
                                  ""
                                }
                                variant="standard"
                                sx={{ width: 80 }}
                                type="string"
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setLocalEvalinkAlerts((prev: any) => ({
                                    ...prev,
                                    [object.id]: {
                                      ...prev[object.id],
                                      partition_id: event.target.value,
                                    },
                                  }));
                                  setUpdatedAlertStreams(
                                    (prev: UpdatedAlertStreams) => ({
                                      ...prev,
                                      evalink: {
                                        ...prev.evalink,
                                        [object.id]: {
                                          ...localEvalinkAlerts?.[object.id],
                                          partition_id: event.target.value,
                                        },
                                      },
                                    })
                                  );
                                  setUpdatedAlertStreamTypes(
                                    (prev: UpdatedAlertStreamTypes) => ({
                                      ...prev,
                                      evalink: true,
                                    })
                                  );
                                }}
                                onBlur={() => {
                                  setCameraStreamsPatchObject(
                                    (previousState: any) => {
                                      const newState = [
                                        ...(previousState || []),
                                      ];
                                      const newStream = {
                                        ...newState[cameraStreamsSelectedIndex],
                                      };
                                      const newAlerts = [
                                        ...(newStream.evalink_alerts || []),
                                      ];
                                      const newAlert = {
                                        ...newAlerts[currentIndex],
                                      };
                                      newAlert.partition_id =
                                        localEvalinkAlerts[object.id]
                                          ?.partition_id || "";
                                      newAlerts[currentIndex] = newAlert;
                                      newStream.evalink_alerts = newAlerts;
                                      newState[cameraStreamsSelectedIndex] =
                                        newStream;
                                      return newState;
                                    }
                                  );
                                }}
                              />
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <TextField
                                value={
                                  localEvalinkAlerts[object.id]
                                    ?.alarm_zone_id || ""
                                }
                                variant="standard"
                                sx={{ width: 80 }}
                                type="string"
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setLocalEvalinkAlerts((prev: any) => ({
                                    ...prev,
                                    [object.id]: {
                                      ...prev[object.id],
                                      alarm_zone_id: event.target.value,
                                    },
                                  }));
                                  setUpdatedAlertStreams(
                                    (prev: UpdatedAlertStreams) => ({
                                      ...prev,
                                      evalink: {
                                        ...prev.evalink,
                                        [object.id]: {
                                          ...localEvalinkAlerts?.[object.id],
                                          alarm_zone_id: event.target.value,
                                        },
                                      },
                                    })
                                  );
                                  setUpdatedAlertStreamTypes(
                                    (prev: UpdatedAlertStreamTypes) => ({
                                      ...prev,
                                      evalink: true,
                                    })
                                  );
                                }}
                                onBlur={() => {
                                  setCameraStreamsPatchObject(
                                    (previousState: any) => {
                                      const newState = [
                                        ...(previousState || []),
                                      ];
                                      const newStream = {
                                        ...newState[cameraStreamsSelectedIndex],
                                      };
                                      const newAlerts = [
                                        ...(newStream.evalink_alerts || []),
                                      ];
                                      const newAlert = {
                                        ...newAlerts[currentIndex],
                                      };
                                      newAlert.alarm_zone_id =
                                        localEvalinkAlerts[object.id]
                                          ?.alarm_zone_id || "";
                                      newAlerts[currentIndex] = newAlert;
                                      newStream.evalink_alerts = newAlerts;
                                      newState[cameraStreamsSelectedIndex] =
                                        newStream;
                                      return newState;
                                    }
                                  );
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {sysaidAlertsOnCamera && (
        <div key="sysaidAlerts" className="pb-[20px]">
          <div className="min-w-[1150px] h-[60px] bg-white">
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{
                fontFamily: "Mulish",
                fontWeight: "Bold",
                color: "black",
                textIndent: "1vw",
                paddingTop: "10px",
                paddingBottom: "10px",
              }}
            >
              SysAid Alerts
            </Typography>
          </div>
          {cameraStreamsPatchObject &&
            cameraStreamsPatchObject[cameraStreamsSelectedIndex] &&
            (cameraStreamsPatchObject[cameraStreamsSelectedIndex]?.sysaid_alerts
              ?.length ?? 0) > 0 &&
            cameraStreamsPatchObject[
              cameraStreamsSelectedIndex
            ]?.sysaid_alerts?.map((object: any, index: any) => {
              const currentIndex = index;
              return (
                <div key={object.id} className="h-[208px]">
                  <div className="min-w-[1150px] h-[80px] bg-white">
                    <div className="flex">
                      <Typography
                        gutterBottom
                        component="div"
                        sx={{
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          color: "black",
                          textIndent: "1vw",
                          paddingTop: "10px",
                        }}
                      >
                        SysAid Alert {index + 1}
                      </Typography>
                    </div>
                  </div>
                  <div>
                    <TableContainer component={Paper}>
                      <Table sx={{ minWidth: 1000, fontFamily: "Mulish" }}>
                        <TableHeadComponent
                          titlesArray={[
                            "SysAid Server",
                            "SysAid Client Local",
                            "SysAid Username",
                            "SysAid Password",
                          ]}
                        />
                        <TableBody>
                          <TableRow key={object.id}>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sysaid_server}>
                                <TextField
                                  id={object.sysaid_server}
                                  value={
                                    localSysaidAlerts[object.id]
                                      ?.sysaid_server || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSysaidAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sysaid_server: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sysaid: {
                                          ...prev.sysaid,
                                          [object.id]: {
                                            ...localSysaidAlerts?.[object.id],
                                            sysaid_server: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sysaid: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sysaid_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sysaid_server =
                                          localSysaidAlerts[object.id]
                                            ?.sysaid_server || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sysaid_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sysaid_client_local}>
                                <TextField
                                  id={object.sysaid_client_local}
                                  value={
                                    localSysaidAlerts[object.id]
                                      ?.sysaid_client_local || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSysaidAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sysaid_client_local: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sysaid: {
                                          ...prev.sysaid,
                                          [object.id]: {
                                            ...localSysaidAlerts?.[object.id],
                                            sysaid_client_local:
                                              event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sysaid: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sysaid_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sysaid_client_local =
                                          localSysaidAlerts[object.id]
                                            ?.sysaid_client_local || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sysaid_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sysaid_username}>
                                <TextField
                                  id={object.sysaid_username}
                                  value={
                                    localSysaidAlerts[object.id]
                                      ?.sysaid_username || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSysaidAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sysaid_username: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sysaid: {
                                          ...prev.sysaid,
                                          [object.id]: {
                                            ...localSysaidAlerts?.[object.id],
                                            sysaid_username: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sysaid: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sysaid_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sysaid_username =
                                          localSysaidAlerts[object.id]
                                            ?.sysaid_username || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sysaid_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontFamily: "Mulish" }}
                            >
                              <div key={object.sysaid_password}>
                                <TextField
                                  id={object.sysaid_password}
                                  value={
                                    localSysaidAlerts[object.id]
                                      ?.sysaid_password || ""
                                  }
                                  variant="standard"
                                  sx={{ width: 150 }}
                                  type="string"
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setLocalSysaidAlerts((prev: any) => ({
                                      ...prev,
                                      [object.id]: {
                                        ...prev[object.id],
                                        sysaid_password: event.target.value,
                                      },
                                    }));
                                    setUpdatedAlertStreams(
                                      (prev: UpdatedAlertStreams) => ({
                                        ...prev,
                                        sysaid: {
                                          ...prev.sysaid,
                                          [object.id]: {
                                            ...localSysaidAlerts?.[object.id],
                                            sysaid_password: event.target.value,
                                          },
                                        },
                                      })
                                    );
                                    setUpdatedAlertStreamTypes(
                                      (prev: UpdatedAlertStreamTypes) => ({
                                        ...prev,
                                        sysaid: true,
                                      })
                                    );
                                  }}
                                  onBlur={() => {
                                    setCameraStreamsPatchObject(
                                      (previousState: any) => {
                                        const newState = [
                                          ...(previousState || []),
                                        ];
                                        const newStream = {
                                          ...newState[
                                            cameraStreamsSelectedIndex
                                          ],
                                        };
                                        const newAlerts = [
                                          ...(newStream.sysaid_alerts || []),
                                        ];
                                        const newAlert = {
                                          ...newAlerts[currentIndex],
                                        };
                                        newAlert.sysaid_password =
                                          localSysaidAlerts[object.id]
                                            ?.sysaid_password || "";
                                        newAlerts[currentIndex] = newAlert;
                                        newStream.sysaid_alerts = newAlerts;
                                        newState[cameraStreamsSelectedIndex] =
                                          newStream;
                                        return newState;
                                      }
                                    );
                                    // setStreamChangesMade(true);
                                  }}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
